import { useContext } from 'react';
import { Text } from '@belong/ui';
import classNames from 'classnames/bind';
import ButtonBase from 'corecomponents/ButtonBase/ButtonBase';
import { find } from 'es-toolkit/compat';
import {
  ConsentStatus,
  MaintenanceAssessment,
  MaintenanceResponsibility,
} from 'models/enums';
import { ExecutionModeCardLabel } from 'pages/PostInspectionFlow/execution-mode/execution-mode-card-label';
import {
  getCardLabel,
  isSelectorGated,
} from 'pages/PostInspectionFlow/execution-mode/utils';
import PropTypes from 'prop-types';
import Checkbox from '../../../../../../../components/Checkbox/Checkbox';
import Spinner from '../../../../../../../components/Spinner/Spinner';
import ImprovementCard from '../../../../../ImprovementCard/ImprovementCard';
import {
  isItemResidentResponsible,
  hasResidentResponsibility,
} from '../../../utils';
import { ApprovalContext } from '../../ApprovalContext';
import styles from './BundleItem.module.css';

const cx = classNames.bind(styles);

export const BundleItem = ({
  isPreventative,
  item,
  onChange,
  onClick,
  bottomRight = null,
  inspectionType,
  improvementPresets,
  isGTTLDisqualifiedDueHomeownerFixingItems,
}) => {
  const {
    loader,
    supportsExecutionMode,
    isComplimentaryRepairsExclusivitySelected:
      _isComplimentaryRepairsExclusivitySelected,
    canExclusivityBeSelected: _canExclusivityBeSelected,
  } = useContext(ApprovalContext);

  const {
    isOnboarding,
    isMoveOut,
    isAdoptedLease: _isAdoptedLease,
  } = inspectionType || {};
  const { maintenancePayments, maintenance } = item;
  const homeownerPayment = maintenancePayments.find(
    (payment) => payment.paidBy === MaintenanceResponsibility.Homeowner,
  );

  const selected =
    (homeownerPayment?.percentage > 0 &&
      homeownerPayment?.consentStatus === ConsentStatus.Approved) ||
    isItemResidentResponsible(item);

  const isResidentResponsible = hasResidentResponsibility(item);

  const isLoading = !!find(
    loader.items,
    (loadingItem) =>
      loadingItem.maintenance.uniqueId === item.maintenance.uniqueId,
  );

  const isDisabled =
    isItemResidentResponsible(item) ||
    item.maintenance.assessment === MaintenanceAssessment.Required;

  const handleLetsDoThisClick = () => onChange(!selected, [item]);

  const handleLetsDoThisSelect = (isImprovementSelected) =>
    onChange(isImprovementSelected, [item]);

  const _handleRespoinsibilityChange = (improvement) => {
    onChange(selected, [improvement]);
  };

  const isHomeownerFixing =
    maintenance.proServiceResponsibility ===
    MaintenanceResponsibility.Homeowner;

  const { allowHomeownerFixing, code } =
    improvementPresets?.[item?.maintenance?.summary] || {};
  const cleaningCodes = ['V106', 'H109'];
  const isDeepCleaning = cleaningCodes.includes(code);

  const isRequired =
    item.maintenance.assessment === MaintenanceAssessment.Required;

  const cardLabel = supportsExecutionMode
    ? getCardLabel(item, isSelectorGated(item, improvementPresets))
    : null;
  const isMuted = cardLabel === 'ownPro' || cardLabel === 'homeownerFixing';
  const showBottomRow = !isRequired || supportsExecutionMode;

  return (
    <div
      className={cx('bundle-item', {
        outline: selected && !isMuted,
        'outline-muted': isMuted,
        'outline-red': isHomeownerFixing && !supportsExecutionMode,
      })}
    >
      {isLoading && (
        <div className={cx('card-spinner')}>
          <Spinner dark />
        </div>
      )}
      <ImprovementCard
        borderRadius={false}
        height={220}
        improvement={item}
        isPreventative={isPreventative}
        mediaContainerClassName={cx('card-image-container', {
          'rounded-[8px]': isRequired && !showBottomRow,
          'dimmed-media': isMuted,
        })}
        onClick={onClick}
        bottomRight={
          typeof bottomRight === 'function'
            ? bottomRight(selected)
            : bottomRight
        }
        inspectionType={inspectionType}
        displayCleaningTooltip={
          allowHomeownerFixing === false && isDeepCleaning
        }
        hideFlag={supportsExecutionMode}
        isHomeownerView
      />

      {showBottomRow && (
        <div
          className={cx('bottom', {
            'justify-end pl-sm pr-2xs -mb-xs': isOnboarding || isMoveOut,
            'justify-between':
              (isHomeownerFixing &&
                isGTTLDisqualifiedDueHomeownerFixingItems) ||
              (isHomeownerFixing && isMoveOut && isResidentResponsible),
          })}
        >
          {supportsExecutionMode && cardLabel && (
            <ButtonBase
              className="flex min-w-0 flex-1 items-center"
              onClick={onClick}
            >
              <ExecutionModeCardLabel label={cardLabel} />
            </ButtonBase>
          )}

          {!isOnboarding && !isMoveOut && (
            <ButtonBase
              className={cx('select-button-wrapper', 'flex justify-start')}
              disabled={isDisabled}
              onClick={handleLetsDoThisClick}
            >
              <Checkbox
                alignCenter
                disabled={isDisabled}
                label="LET'S DO THIS"
                onSelect={handleLetsDoThisSelect}
                useOnChangeFormToHandle
                value={selected}
              />
            </ButtonBase>
          )}

          <ButtonBase
            className={cx('show-details-button', 'place-self-end')}
            onClick={onClick}
          >
            <Text fontWeight="semibold">DETAILS</Text>
          </ButtonBase>
        </div>
      )}
    </div>
  );
};

BundleItem.propTypes = {
  isPreventative: PropTypes.bool,
  item: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  isGTTLDisqualifiedDueHomeownerFixingItems: PropTypes.bool,
  bottomRight: PropTypes.node,
  inspectionType: PropTypes.object,
  improvementPresets: PropTypes.object,
};
